import { createAsyncThunk } from '@reduxjs/toolkit';

import { getErrorMessage } from '@/shared/utils/error';

import type {
  CancelarFaturaInput,
  ComprovanteInput,
  DescontoInput,
  ErroFinanceiro,
  EstornoInput,
  EstornoSolicitado,
  FaturaDoProcesso,
  LinhaAr,
  PaginaFinanceiro,
  ResumoFinanceiroProcesso,
  SegundaViaInput,
  SolicitacaoEstornoInput,
  ZerarConsumoInput,
} from '../types';
import { financeiroProcessoService } from './services';

const TAMANHO_PAGINA_FINANCEIRO = 20;

function comoErro(error: unknown): ErroFinanceiro {
  return { mensagem: getErrorMessage(error) };
}

export const fetchResumoFinanceiro = createAsyncThunk<
  { processoId: string; resumo: ResumoFinanceiroProcesso },
  string,
  { rejectValue: ErroFinanceiro }
>('financeiroProcessoArbitral/fetchResumo', async (processoId, { rejectWithValue }) => {
  try {
    return { processoId, resumo: await financeiroProcessoService.obterResumo(processoId) };
  } catch (error: unknown) {
    return rejectWithValue(comoErro(error));
  }
});

export const fetchFaturasProcesso = createAsyncThunk<
  { processoId: string; pagina: PaginaFinanceiro<FaturaDoProcesso>; acumular: boolean },
  { processoId: string; page?: number; acumular?: boolean },
  { rejectValue: ErroFinanceiro }
>(
  'financeiroProcessoArbitral/fetchFaturas',
  async ({ processoId, page = 1, acumular = false }, { rejectWithValue }) => {
    try {
      const pagina = await financeiroProcessoService.listarFaturas(
        processoId,
        page,
        TAMANHO_PAGINA_FINANCEIRO,
      );
      return { processoId, pagina, acumular };
    } catch (error: unknown) {
      return rejectWithValue(comoErro(error));
    }
  },
);

export const fetchArsProcesso = createAsyncThunk<
  { processoId: string; pagina: PaginaFinanceiro<LinhaAr>; acumular: boolean },
  { processoId: string; page?: number; acumular?: boolean },
  { rejectValue: ErroFinanceiro }
>(
  'financeiroProcessoArbitral/fetchArs',
  async ({ processoId, page = 1, acumular = false }, { rejectWithValue }) => {
    try {
      const pagina = await financeiroProcessoService.listarArs(
        processoId,
        page,
        TAMANHO_PAGINA_FINANCEIRO,
      );
      return { processoId, pagina, acumular };
    } catch (error: unknown) {
      return rejectWithValue(comoErro(error));
    }
  },
);

async function recarregar(processoId: string, dispatch: (acao: unknown) => unknown): Promise<void> {
  await Promise.all([
    dispatch(fetchResumoFinanceiro(processoId)),
    dispatch(fetchFaturasProcesso({ processoId })),
    dispatch(fetchArsProcesso({ processoId })),
  ]);
}

export const cancelarFaturaProcesso = createAsyncThunk<
  FaturaDoProcesso,
  CancelarFaturaInput,
  { rejectValue: ErroFinanceiro }
>('financeiroProcessoArbitral/cancelarFatura', async (input, { dispatch, rejectWithValue }) => {
  try {
    const fatura = await financeiroProcessoService.cancelarFatura(input);
    await recarregar(input.processoId, dispatch);
    return fatura;
  } catch (error: unknown) {
    return rejectWithValue(comoErro(error));
  }
});

export const enviarSegundaViaProcesso = createAsyncThunk<
  FaturaDoProcesso,
  SegundaViaInput,
  { rejectValue: ErroFinanceiro }
>('financeiroProcessoArbitral/enviarSegundaVia', async (input, { dispatch, rejectWithValue }) => {
  try {
    const fatura = await financeiroProcessoService.enviarSegundaVia(input);
    await recarregar(input.processoId, dispatch);
    return fatura;
  } catch (error: unknown) {
    return rejectWithValue(comoErro(error));
  }
});

export const anexarComprovanteProcesso = createAsyncThunk<
  FaturaDoProcesso,
  ComprovanteInput,
  { rejectValue: ErroFinanceiro }
>('financeiroProcessoArbitral/anexarComprovante', async (input, { dispatch, rejectWithValue }) => {
  try {
    const fatura = await financeiroProcessoService.anexarComprovante(input);
    await recarregar(input.processoId, dispatch);
    return fatura;
  } catch (error: unknown) {
    return rejectWithValue(comoErro(error));
  }
});

export const aplicarDescontoProcesso = createAsyncThunk<
  FaturaDoProcesso,
  DescontoInput,
  { rejectValue: ErroFinanceiro }
>('financeiroProcessoArbitral/aplicarDesconto', async (input, { dispatch, rejectWithValue }) => {
  try {
    const fatura = await financeiroProcessoService.aplicarDesconto(input);
    await recarregar(input.processoId, dispatch);
    return fatura;
  } catch (error: unknown) {
    return rejectWithValue(comoErro(error));
  }
});

export const estornarPagamentoProcesso = createAsyncThunk<
  EstornoSolicitado,
  EstornoInput,
  { rejectValue: ErroFinanceiro }
>('financeiroProcessoArbitral/estornarPagamento', async (input, { dispatch, rejectWithValue }) => {
  try {
    const resultado = await financeiroProcessoService.estornarPagamento(input);
    await recarregar(input.processoId, dispatch);
    return resultado;
  } catch (error: unknown) {
    return rejectWithValue(comoErro(error));
  }
});

export const solicitarEstornoProcesso = createAsyncThunk<
  FaturaDoProcesso,
  SolicitacaoEstornoInput,
  { rejectValue: ErroFinanceiro }
>('financeiroProcessoArbitral/solicitarEstorno', async (input, { dispatch, rejectWithValue }) => {
  try {
    const fatura = await financeiroProcessoService.solicitarEstorno(input);
    await recarregar(input.processoId, dispatch);
    return fatura;
  } catch (error: unknown) {
    return rejectWithValue(comoErro(error));
  }
});

export const zerarConsumoProcesso = createAsyncThunk<
  { eventoId: string },
  ZerarConsumoInput,
  { rejectValue: ErroFinanceiro }
>('financeiroProcessoArbitral/zerarConsumo', async (input, { dispatch, rejectWithValue }) => {
  try {
    const resultado = await financeiroProcessoService.zerarConsumo(input);
    await recarregar(input.processoId, dispatch);
    return resultado;
  } catch (error: unknown) {
    return rejectWithValue(comoErro(error));
  }
});

export const reativarConsumoProcesso = createAsyncThunk<
  { eventoId: string },
  ZerarConsumoInput,
  { rejectValue: ErroFinanceiro }
>('financeiroProcessoArbitral/reativarConsumo', async (input, { dispatch, rejectWithValue }) => {
  try {
    const resultado = await financeiroProcessoService.reativarConsumo(input);
    await recarregar(input.processoId, dispatch);
    return resultado;
  } catch (error: unknown) {
    return rejectWithValue(comoErro(error));
  }
});
